import {Injectable} from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';

import {finalize, Observable, tap, throwError} from 'rxjs';
import swal from "sweetalert2";
import {catchError} from "rxjs/operators";
import {ToastrService} from "ngx-toastr";
import {UtilsService} from "../core/services/utils.service";
import {Outcome} from "../core/interfaces/common.interface";
import {TranslateService} from "@ngx-translate/core";
import {CALL_CTX, CallContext, ShowMessage} from "./context-tokens";
import {LogLevel} from "../core/interfaces/admin.interface";
import {LoggingService} from "../core/services/log.service";


/**

 This is an Angular HTTP interceptor that handles API call responses and provides user feedback through loading indicators and toast notifications. Here's what it does:

 ## Core Functionality

 **Request/Response Flow:**
 - Intercepts all HTTP requests before they're sent and responses when they return
 - Shows a loading modal (using SweetAlert2) when a request starts
 - Processes the response to show appropriate success/error messages
 - Closes the loading modal when the request completes

 ## Message Display Logic

 **Success Messages:**
 - Shows success toasts for non-GET requests (POST, PUT, DELETE, etc.)
 - Skips success messages for specific URLs (like `/all`, `/filter`, `/login`)
 - Can show persistent success modals or temporary toasts based on context
 - Uses translated messages from the translation service

 **Error Handling:**
 - Displays error messages using toast notifications
 - Has two types of error handling:
 1. **Validation errors**: Shows formatted field-level validation violations with nested lists
 2. **General errors**: Shows generic error messages with optional persistence

 **Silent Mode:**
 - Certain URLs (like `/configuration/load_file`) suppress error messages entirely
 - Logs errors but doesn't show them to users

 ## Context-Based Behavior

 The interceptor respects a `CallContext` object that can be attached to requests, allowing per-request customization:
 - `muteMessages`: Completely suppress all messages
 - `showSuccessMessage`: Override default success message behavior
 - `persistentSuccessMessage`: Show modal instead of toast for success
 - `persistentErrorMessage`: Show persistent toast that doesn't auto-dismiss

 ## Validation Error Formatting

 When validation errors occur, it creates a nicely formatted HTML display:
 - Groups violations by field name
 - Formats camelCase field names into readable text
 - Shows root-level violations separately
 - Uses field labels if provided by the backend
 - Escapes HTML to prevent XSS attacks

 ## Logging

 All operations are logged through a logging service with appropriate log levels for debugging and monitoring.

 */
/** Pass untouched request through to the next request handler. */
@Injectable()
export class CallInterceptor implements HttpInterceptor {

    private silentErrorUrls = [
        '/configuration/load_file'
    ]

    private silentSuccessUrls = [
        '/all',
        '/filter',
        '/next',
        '/all/summary',
        '/login',
    ]

    constructor(
        private toaster: ToastrService,
        private utils: UtilsService,
        private logService: LoggingService,
        private translate: TranslateService,
    ) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler):
        Observable<HttpEvent<any>> {
        const ctx = req?.context.get(CALL_CTX);
        this.showLoading(ctx);
        const hideLoader = { val: true };
        return next.handle(req)
            .pipe(tap(event => {
                if (event instanceof HttpResponse) {
                    if (event.body.outcome) {
                        const outcome = event.body.outcome
                        if(this.isSilent(req)) {
                            console.log('silent')
                        }
                        if (outcome.success) {
                            if (this.showSuccessMessage(req, ctx)) {
                                const msg = outcome.message != null ? outcome.message : this.translate.instant('swal.op_success')
                                this.logService.log(`[ERROR1] ${req.url}`, msg, LogLevel.DEBUG);
                                if(this.isPersistentSuccessMessage(ctx)) {
                                    swal.fire(msg, "", "success");
                                    hideLoader.val = false
                                }
                                else
                                    this.toaster.success(msg)
                            }
                        } else {
                            if(this.isSilent(req))
                                return;
                            if(this.showErrorMessage(ctx)) {
                                if (outcome.violations && Object.keys(outcome.violations).length > 0) {
                                    this.handleValidationError(outcome, ctx, hideLoader);
                                    hideLoader.val = true
                                }
                                else {
                                    const msg = outcome.provided ? Outcome.toError(outcome) : this.utils.manageErrorMsg(outcome);
                                    this.logService.log(`[ERROR] ${req.url}`, msg, LogLevel.ALL);
                                    if (msg != null) {
                                        const mainMsg = this.translate.instant('swal.op_error');
                                        if(this.isPersistentErrorMessage(ctx)) {
                                            this.toaster.error(`${mainMsg}: ${msg}`, 'Errore', {
                                                disableTimeOut: true
                                            })
                                            // hideLoader.val = false
                                        }
                                        else {
                                            this.toaster.error(`${mainMsg}: ${msg}`)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }))
            .pipe(catchError((error: HttpErrorResponse) => {
                console.error('Logging Interceptor DI Error:', error);
                return throwError(() => error);
            }))
            .pipe(finalize(() => {
                if (hideLoader.val)
                    swal.close();
            }));
    }

    isSilent(req: HttpRequest<any>): boolean {
        const path = req.url.split('webapp')
        if(path.length > 1) {
            if(this.silentErrorUrls.includes(path[1]))
                return true;
        }
        return false;
    }

    showLoading(ctx: CallContext): boolean {
        // if (ctx) {
            swal.fire({
                title: this.translate.instant('swal.op_in_progress'),
                didOpen: function () {
                    swal.showLoading();
                },
            });
        // } else {
        //     swal.showLoading(null)
        // }
        return true;
    }

    isPersistentSuccessMessage(ctx: CallContext): boolean {
        return ctx && ctx.persistentSuccessMessage
    }

    isPersistentErrorMessage(ctx: CallContext): boolean {
        return !ctx || ctx.persistentSuccessMessage
    }

    showSuccessMessage(req: HttpRequest<any>, ctx: CallContext): boolean {
        if(ctx && ctx.muteMessages)
            return false;
        const defaultHandling = !ctx || ctx.showSuccessMessage == ShowMessage.default
        if (defaultHandling) {
            return req.method != 'GET' &&
                this.silentSuccessUrls.filter(u => req.url.includes(u)).length == 0;
            // return req.method != 'GET' && !(req.url.includes('/all') || req.url.endsWith('/filter')
            //     || req.url.endsWith('/next') || req.url.endsWith('/all/summary'));
        }
        else
            return ctx.showSuccessMessage == ShowMessage.show;
    }

    showErrorMessage(ctx: CallContext): boolean {
        if(ctx && ctx.muteMessages)
            return false;
        return !ctx || ctx.showSuccessMessage == ShowMessage.show || ctx.showSuccessMessage == ShowMessage.default
    }

    private handleValidationError(
        outcome: any,
        ctx: CallContext,
        hideLoader: { val: boolean }
    ): void {
        const htmlMessage = this.buildValidationErrorHtml(outcome.violations);
        const title = outcome.message;

        this.logService.log('[VALIDATION ERROR]', JSON.stringify(outcome.fieldViolations), LogLevel.ALL);

        if (this.isPersistentErrorMessage(ctx)) {
            this.toaster.error(htmlMessage, title, {
                enableHtml: true,
                disableTimeOut: true,
                // closeButton: true
            });
        } else {
            this.toaster.error(htmlMessage, title, {
                enableHtml: true,
                timeOut: 7000,
                // closeButton: true,
                progressBar: true
            });
        }
        hideLoader.val = false;
    }

    private buildValidationErrorHtml(fieldViolations: any): string {
        let html = '<div style="font-family: Arial, sans-serif; font-size: 14px;">';
        html += '<ul style="margin: 0; padding-left: 20px; list-style-type: none;">';

        Object.keys(fieldViolations).forEach(field => {
            const violations = fieldViolations[field];

            if (violations && Array.isArray(violations.violations)) {
                // Handle root-level violations differently
                if (field === 'root' || field === '_root' || field === '') {
                    violations.violations.forEach(violation => {
                        const message = violation.message || violation;
                        html += `
                        <li style="margin-bottom: 8px;">
                            <span style="color: #dc3545;">⚠</span> 
                            ${this.escapeHtml(message)}
                        </li>
                    `;
                    });
                } else {
                    // Format field name nicely
                    const displayField = violations.label ? violations.label : this.formatFieldName(field);

                    // Show field name once
                    html += `
                    <li style="margin-bottom: 12px;">
                        <strong style="color: #333;">${displayField}:</strong>
                        <ul style="margin: 4px 0 0 0; padding-left: 20px; list-style-type: disc;">
                `;

                    // Add all violations as nested list items
                    violations.violations.forEach(violation => {
                        const message = violation.message || violation;
                        html += `
                        <li style="margin-bottom: 4px; color: #dc3545;">
                            ${this.escapeHtml(message)}
                        </li>
                    `;
                    });

                    html += `
                        </ul>
                    </li>
                `;
                }
            }
        });

        html += '</ul></div>';
        return html;
    }


    /**
     * Format field names for display.
     * Examples:
     * - "root.field" -> "Field"
     * - "customerPayment" -> "Customer Payment"
     * - "address.city" -> "Address City"
     */
    private formatFieldName(field: string): string {
        // Remove 'root.' prefix if present
        let cleaned = field.replace(/^(root\.|_root\.)/, '');

        // Replace dots with spaces
        cleaned = cleaned.replace(/\./g, ' ');

        // Convert camelCase to separate words
        cleaned = cleaned.replace(/([a-z])([A-Z])/g, '$1 $2');

        // Capitalize first letter of each word
        return cleaned
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /**
     * Escape HTML to prevent XSS attacks.
     * Only use this for untrusted content from the backend.
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

}

